export class ApiClientError extends Error {
  readonly code: null | string;
  readonly status: null | number;

  constructor(input: {
    code: null | string;
    message: string;
    status: null | number;
  }) {
    super(input.message);
    this.name = 'ApiClientError';
    this.code = input.code;
    this.status = input.status;
  }
}

export function unwrapApiResult<TData>(input: {
  fallbackMessage: string;
  result: {
    data: null | TData;
    error: unknown;
  };
}) {
  if (input.result.error) {
    throw toApiClientError({
      error: input.result.error,
      fallbackMessage: input.fallbackMessage,
    });
  }

  if (input.result.data === null) {
    throw new Error(input.fallbackMessage);
  }

  return input.result.data;
}

function toApiClientError(input: { error: unknown; fallbackMessage: string }) {
  const errorRecord = readRecord(input.error);
  const errorValue = errorRecord
    ? readRecordProperty({ key: 'value', record: errorRecord })
    : null;
  const bodyRecord = readRecord(errorValue);
  const status =
    (errorRecord
      ? readNumber(readRecordProperty({ key: 'status', record: errorRecord }))
      : null) ??
    (bodyRecord
      ? readNumber(readRecordProperty({ key: 'status', record: bodyRecord }))
      : null);
  const code =
    (bodyRecord
      ? readString(readRecordProperty({ key: 'code', record: bodyRecord }))
      : null) ??
    (bodyRecord
      ? readString(readRecordProperty({ key: 'error', record: bodyRecord }))
      : null);
  const message =
    (bodyRecord
      ? readString(readRecordProperty({ key: 'message', record: bodyRecord }))
      : null) ??
    (bodyRecord
      ? readString(readRecordProperty({ key: 'error', record: bodyRecord }))
      : null) ??
    (errorRecord
      ? readString(readRecordProperty({ key: 'message', record: errorRecord }))
      : null) ??
    (typeof errorValue === 'string' && errorValue.length > 0
      ? errorValue
      : null) ??
    input.fallbackMessage;

  return new ApiClientError({
    code,
    message,
    status,
  });
}

function readNumber(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value;
}

function readRecord(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
}

function readRecordProperty(input: { key: string; record: object }) {
  return Reflect.get(input.record, input.key);
}

function readString(value: unknown) {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  return value;
}
